import { FunctionComponent, useCallback, useMemo, useState } from 'react'

import styled, { useTheme } from 'styled-components'

import translate from '@i18n'
import useBlickPlusSkeleton from '@hooks/useBlickPlusSkeleton'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import Text from '@components/PlusLinkShare/Text'
import { copyToClipboard } from '@components/Social/utils'

import LinkToFaqs from './LinkToFaqs'
import Spinner from './Loading/Spinner'
import { TextAndButtonContainerProps } from './types'

const CopyButton = styled(PrimaryCTAButton)`
  width: 100%;
`

const TextAndButtonContainer: FunctionComponent<
  TextAndButtonContainerProps
> = ({
  onGenerateButtonClick,
  linkToCopy,
  updatedAvailableShares,
  isLinkLoading,
  isError,
  availableShares,
}) => {
  const theme = useTheme()
  const { plusSharingLinkGenerationAmount } = useBlickPlusSkeleton()
  const [isLinkCopied, setIsLinkCopied] = useState<boolean>(false)

  const hasRunOutOfShares = useMemo<boolean>(
    () => availableShares === 0,
    [availableShares],
  )

  const hasRunOutOfSharesText = translate(
    'blickPlus.linkSharing.hasRunOutOfSharesText',
  )

  const availableSharesText = useMemo<string>(
    () =>
      translate('blickPlus.linkSharing.availableSharesText', {
        linkAmount: `${plusSharingLinkGenerationAmount}`,
      }),

    [plusSharingLinkGenerationAmount],
  )

  const onCopyButtonClick = useCallback(() => {
    if (linkToCopy) {
      copyToClipboard(linkToCopy).then(() => {
        setIsLinkCopied(true)
      })
    }
  }, [linkToCopy])

  const buttonContent = useMemo(() => {
    if (isLinkLoading) {
      return <Spinner size={24} color={theme.colors.staticWhite} />
    }
    if (typeof updatedAvailableShares !== 'number') {
      return translate('blickPlus.linkSharing.copyButton.generate')
    }
    if (isLinkCopied) {
      return translate('blickPlus.linkSharing.copyButton.copied')
    }
    return translate('blickPlus.linkSharing.copyButton.copy')
  }, [
    updatedAvailableShares,
    theme.colors.staticWhite,
    isLinkLoading,
    isLinkCopied,
  ])

  return (
    <>
      <Text>
        {hasRunOutOfShares ? hasRunOutOfSharesText : availableSharesText}
      </Text>
      <LinkToFaqs />
      <CopyButton
        type="button"
        size="small"
        onClick={!!linkToCopy ? onCopyButtonClick : onGenerateButtonClick}
        disabled={hasRunOutOfShares || isError || isLinkLoading || isLinkCopied}
      >
        {buttonContent}
      </CopyButton>
    </>
  )
}

export default TextAndButtonContainer
